import { useEffect, useMemo, useState } from "react";
import { LayoutGroup, motion } from "framer-motion";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import { fetchRecipes } from "@/lib/api";
import RecipeCard from "@/components/marketing/RecipeCard";
import ScrollReveal from "@/components/marketing/ScrollReveal";
import { cn } from "@/lib/utils";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const FILTERS = [
  { key: "all", label: "All" },
  { key: "breakfast", label: "Breakfast" },
  { key: "everyday", label: "Everyday meals", match: ["everyday", "tiffin", "tadka"] },
  { key: "vegetarian", label: "Vegetarian" },
  { key: "kerala", label: "Kerala" },
  { key: "karnataka", label: "Karnataka" },
  { key: "coastal", label: "Coastal and fish", match: ["coastal", "fish"] },
  { key: "sweet", label: "Sweets, snacks and baking", match: ["sweet", "snack", "baking", "frying"] },
];

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => { fetchRecipes().then((d) => setRecipes(d.recipes || [])).catch(() => {}); }, []);

  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    if (!f || f.key === "all") return recipes;
    const tags = f.match || [f.key];
    return recipes.filter((r) => (r.tags || []).some((t) => tags.includes(String(t).toLowerCase())));
  }, [recipes, filter]);

  const jsonLd = [
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Recipes", path: "/recipes" }]),
    ...(recipes.length ? [{
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Karijeeva coconut oil recipes",
      itemListElement: recipes.map((r, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE_URL}/recipes/${r.slug}`, name: r.title })),
    }] : []),
  ];

  return (
    <>
      <Seo
        title="Coconut Oil Recipes from Karnataka and Kerala Kitchens"
        description="Everyday South Indian recipes cooked with Karijeeva cold pressed coconut oil: dosa, appam, neer dosa, avial, poriyal, meen curry, banana chips, chutney and more."
        jsonLd={jsonLd}
      />
      <section className="bg-brand-parchment-soft border-b border-brand-gold/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <p className="eyebrow text-brand-gold tracking-[0.3em] mb-3">In the kitchen</p>
          <h1 className="text-h1 text-brand-obsidian">Recipes from Karnataka and Kerala kitchens.</h1>
          <p className="font-body text-body-lg text-ink-muted mt-5 max-w-2xl">
            {recipes.length ? `${recipes.length} everyday dishes` : "Everyday dishes"} where coconut oil does what it does best: the tadka, the dosa tawa, the finishing spoon over avial, the kadai of banana chips.
          </p>
          <LayoutGroup id="recipe-filter">
            <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Filter recipes">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  className={cn("relative rounded-control px-4 py-2 font-body text-[14px] transition-colors", filter === f.key ? "text-white" : "text-brand-ink bg-white hover:bg-black/5")}
                  data-testid={`recipe-filter-${f.key}`}
                >
                  {filter === f.key && <motion.span layoutId="recipe-filter-pill" className="absolute inset-0 rounded-control bg-brand-gold" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                  <span className="relative">{f.label}</span>
                </button>
              ))}
            </div>
          </LayoutGroup>
        </div>
      </section>
      <section className="bg-brand-parchment">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="recipes-grid">
            {shown.map((r, i) => (
              <ScrollReveal key={r.slug} delay={(i % 3) * 80}>
                <RecipeCard recipe={r} />
              </ScrollReveal>
            ))}
          </div>
          {recipes.length > 0 && shown.length === 0 && (
            <p className="font-body text-ink-muted text-center">No recipes in this group yet.</p>
          )}
        </div>
      </section>
    </>
  );
}
